import styles from './scrolling-text.module.css'

import cn from 'clsx'
import React, { useCallback, useEffect, useRef, useState } from 'react'

const SCROLL_SPEED_PX_PER_SECOND = 40
const MIN_SCROLL_DURATION_SECONDS = 0.4
const RETURN_DURATION_SECONDS = 0.25
// Small grace period so the clamp is never restored on the frame before the
// slide back settles, which would show up as a last-moment jump.
const RESTORE_BUFFER_MS = 50
// Sweeping the pointer across the list shouldn't flick the ellipsis off on
// every row it crosses.
const HOVER_INTENT_MS = 100

type ScrollingTextProps = {
  children: React.ReactNode
  className?: string
  /** Put the component itself in the tab order. Only needed when the content
   * has no focusable element of its own; with one (e.g. a link), this would
   * add a second tab stop. */
  focusable?: boolean
}

const prefersReducedMotion = () =>
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)

/**
 * Truncates its content with an ellipsis, and slides it sideways on hover or
 * focus to reveal the rest when it doesn't fit.
 *
 * The keyboard reveal relies on focus reaching the component: it fires from a
 * focusable child (e.g. a link), or via `focusable` for plain-text content.
 */
export const ScrollingText = ({
  children,
  className,
  focusable = false,
}: ScrollingTextProps) => {
  const viewportRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLSpanElement>(null)
  const restoreTimer = useRef<ReturnType<typeof setTimeout>>()
  const intentTimer = useRef<ReturnType<typeof setTimeout>>()
  // Hover and focus each hold the reveal open on their own; the scroll only
  // stops once neither is active, so a stray pointer exit can't collapse the
  // text out from under keyboard focus (or vice versa).
  const isHovered = useRef(false)
  const isFocused = useRef(false)
  // True from the first real slide until the clamp is restored, so the stop
  // path is a no-op for rows that never scrolled.
  const isEngaged = useRef(false)

  // Two states, not one: `isSlid` drives the transform, `isExpanded` drops the
  // width clamp and the ellipsis. The clamp has to stay off until the slide
  // back has finished — re-clamping mid-transition re-truncates the text and it
  // reads as a jump to the left followed by a crawl home.
  const [isExpanded, setIsExpanded] = useState(false)
  const [isSlid, setIsSlid] = useState(false)

  useEffect(
    () => () => {
      clearTimeout(restoreTimer.current)
      clearTimeout(intentTimer.current)
    },
    [],
  )

  const startScrolling = useCallback(() => {
    const text = textRef.current
    const viewport = viewportRef.current
    if (!text || !viewport) return

    // Under prefers-reduced-motion the reveal still happens — the CSS drops the
    // transition, so it lands as a discrete jump rather than not at all.
    clearTimeout(restoreTimer.current)

    // Measured against the viewport rather than the text's own client width, so
    // it still works when re-entering mid-return with the clamp already off.
    const overflow = text.scrollWidth - viewport.clientWidth
    if (overflow <= 0) return

    // Constant reading speed rather than a fixed duration, so longer names
    // don't slide past faster than they can be read.
    const duration = Math.max(
      overflow / SCROLL_SPEED_PX_PER_SECOND,
      MIN_SCROLL_DURATION_SECONDS,
    )
    text.style.setProperty('--scroll-distance', `-${overflow}px`)
    text.style.setProperty('--scroll-duration', `${duration}s`)

    isEngaged.current = true
    setIsExpanded(true)
    setIsSlid(true)
  }, [])

  const stopScrolling = useCallback(() => {
    if (!isEngaged.current) return

    // A restore may already be pending from an earlier leave in the same
    // engagement; without this it would be orphaned and fire mid-slide.
    clearTimeout(restoreTimer.current)

    const text = textRef.current

    // Come back quickly from wherever the slide got to; reusing the outbound
    // duration makes the return crawl, since that duration scales with name
    // length rather than with the distance left to travel.
    const returnSeconds = prefersReducedMotion() ? 0 : RETURN_DURATION_SECONDS
    text?.style.setProperty('--scroll-duration', `${returnSeconds}s`)

    setIsSlid(false)
    restoreTimer.current = setTimeout(() => {
      isEngaged.current = false
      setIsExpanded(false)
    }, returnSeconds * 1000 + RESTORE_BUFFER_MS)
  }, [])

  const handleMouseEnter = useCallback(() => {
    isHovered.current = true
    clearTimeout(intentTimer.current)
    intentTimer.current = setTimeout(startScrolling, HOVER_INTENT_MS)
  }, [startScrolling])

  const handleMouseLeave = useCallback(() => {
    isHovered.current = false
    clearTimeout(intentTimer.current)
    if (!isFocused.current) stopScrolling()
  }, [stopScrolling])

  const handleFocus = useCallback(() => {
    isFocused.current = true
    startScrolling()
  }, [startScrolling])

  const handleBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      // Focus moving between links inside keeps the reveal open.
      if (viewportRef.current?.contains(event.relatedTarget as Node | null)) {
        return
      }
      isFocused.current = false
      if (!isHovered.current) stopScrolling()
    },
    [stopScrolling],
  )

  return (
    <div
      ref={viewportRef}
      className={cn(styles.viewport, className)}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- opt-in only: plain-text content has no focusable child, so this is the only way the keyboard can reach the reveal
      tabIndex={focusable ? 0 : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <span
        ref={textRef}
        className={styles.text}
        data-expanded={isExpanded ? 'true' : undefined}
        data-scrolling={isSlid ? 'true' : undefined}
      >
        {children}
      </span>
    </div>
  )
}

export default ScrollingText
